import * as Sentry from '@sentry/nuxt'

/**
 * Composable to interact with Sentry for error tracking & monitoring.
 *
 * When Sentry is disabled (via env config), all methods are safe no-ops.
 *
 * @example
 * ```ts
 * const { captureException, captureMessage, setUser, clearUser, addBreadcrumb } = useSentry()
 *
 * // Capture an error
 * captureException(new Error('Something went wrong'))
 *
 * // Capture a message
 * captureMessage('User performed action X')
 *
 * // Set user context after login
 * setUser({ id: '123', email: 'user@example.com' })
 *
 * // Clear user context after logout
 * clearUser()
 *
 * // Add a breadcrumb for debugging
 * addBreadcrumb({ category: 'navigation', message: 'User navigated to /dashboard' })
 * ```
 */
export function useSentry() {
  const { sentry: sentryConfig } = useRuntimeConfig().public
  const isActive = Boolean(sentryConfig.enabled && sentryConfig.dsn)

  /**
   * Capture an exception and send it to Sentry.
   */
  function captureException(error: unknown, context?: Sentry.ExclusiveEventHintOrCaptureContext) {
    if (!isActive) {
      return
    }
    Sentry.captureException(error, context)
  }

  /**
   * Capture a message and send it to Sentry.
   */
  function captureMessage(message: string, level?: Sentry.SeverityLevel) {
    if (!isActive) {
      return
    }
    Sentry.captureMessage(message, level)
  }

  /**
   * Set the user context for Sentry (e.g., after login).
   */
  function setUser(user: Sentry.User | null) {
    if (!isActive) {
      return
    }
    Sentry.setUser(user)
  }

  /**
   * Clear the user context (e.g., after logout).
   */
  function clearUser() {
    if (!isActive) {
      return
    }
    Sentry.setUser(null)
  }

  /**
   * Add a breadcrumb for better debugging context.
   */
  function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
    if (!isActive) {
      return
    }
    Sentry.addBreadcrumb(breadcrumb)
  }

  /**
   * Set a tag on all future events.
   */
  function setTag(key: string, value: string) {
    if (!isActive) {
      return
    }
    Sentry.setTag(key, value)
  }

  /**
   * Set extra context data on all future events.
   */
  function setExtra(key: string, value: unknown) {
    if (!isActive) {
      return
    }
    Sentry.setExtra(key, value)
  }

  /**
   * Set the entire context for a given context name.
   */
  function setContext(name: string, context: Record<string, unknown> | null) {
    if (!isActive) {
      return
    }
    Sentry.setContext(name, context)
  }

  return {
    /** Whether Sentry is active and sending events */
    isActive,
    captureException,
    captureMessage,
    setUser,
    clearUser,
    addBreadcrumb,
    setTag,
    setExtra,
    setContext,
  }
}
